import type { IamAccessListFilters, IamAccessListItem, IamAccessSortBy, IamTenantSortBy, IamTenantsListFilters, IamUsersListFilters, InviteListFilters, InviteListItem, TenantListItem, UserItem } from "@/features/identity-hub/api/identity-hub.api";
import type { RoleRow, RoleSortKey, RoleSortState } from "@/features/identity-hub/components/dashboard/RolesTable";
import { industryLabel } from "@/features/identity-hub/components/dashboard/OrgCard";
import type { OrgFilters } from "@/features/identity-hub/components/dashboard/OrgToolbar";
import type { OrgSortKey, OrgSortState } from "@/features/identity-hub/components/dashboard/OrgsTable";
import type { PeopleFilters, SortState } from "@/features/identity-hub/components/dashboard/PeopleTable";
import type { InvitesFilters } from "@/features/identity-hub/components/dashboard/InvitesToolbar";
import type { InviteSortState } from "@/features/identity-hub/components/dashboard/InvitesTable";
import type { RoleFilters } from "@/features/identity-hub/components/dashboard/RolesToolbar";

export function industryLabelToTenantType(label: string): IamTenantsListFilters["tenantType"] | undefined {
  if (label === industryLabel("SUPER_TENANT")) return "SUPER_TENANT";
  if (label === industryLabel("API")) return "API";
  if (label === industryLabel("ORGANIZATION")) return "ORGANIZATION";
  return undefined;
}

export function toIamUsersListFilters(
  filters: PeopleFilters,
  sort: SortState,
  searchOverride?: string,
): IamUsersListFilters {
  return {
    search: (searchOverride ?? filters.search).trim() || undefined,
    tenantId: filters.orgId ?? undefined,
    accessName: filters.role ?? undefined,
    status: filters.status ?? undefined,
    noRole: filters.noRole || undefined,
    neverSignedIn: filters.neverSignedIn || undefined,
    hasPendingInvite: filters.hasPendingInvite || undefined,
    sortBy: sort.key as IamUsersListFilters["sortBy"],
    sortOrder: sort.dir,
    limit: 50,
  };
}

/** Default people tab state for super-tenant mount / route prefetch (no org scope). */
export const DEFAULT_PEOPLE_FILTERS: PeopleFilters = {
  search: "",
  orgId: null,
  role: null,
  status: null,
  noRole: false,
  neverSignedIn: false,
  hasPendingInvite: false,
};

export const DEFAULT_PEOPLE_SORT: SortState = { key: "name", dir: "asc" };

export function defaultIamUsersListFilters(
  filters: PeopleFilters = DEFAULT_PEOPLE_FILTERS,
  sort: SortState = DEFAULT_PEOPLE_SORT,
): IamUsersListFilters {
  return toIamUsersListFilters(filters, sort);
}

/** Route prefetch: scope people list to active tenant for non-super-tenant users. */
export function defaultIamUsersListFiltersForSession(
  activeTenantId: string | null,
  isSuperTenant: boolean,
): IamUsersListFilters {
  const filters: PeopleFilters = isSuperTenant
    ? DEFAULT_PEOPLE_FILTERS
    : { ...DEFAULT_PEOPLE_FILTERS, orgId: activeTenantId };
  return defaultIamUsersListFilters(filters);
}

/** Maps the UI sort key to the server enum so `OrgSortKey` never leaks to the API. */
const ORG_SORT_TO_SERVER: Record<OrgSortKey, IamTenantSortBy> = {
  name: "name",
  members: "memberCount",
  createdAt: "createdAt",
};

export const DEFAULT_ORG_SORT: OrgSortState = { key: "name", dir: "asc" };

export function toIamTenantsListFilters(
  filters: OrgFilters,
  opts: { includeArchived: boolean; searchOverride?: string; sort?: OrgSortState },
): IamTenantsListFilters {
  const sort = opts.sort ?? DEFAULT_ORG_SORT;
  return {
    search: (opts.searchOverride ?? filters.search).trim() || undefined,
    tenantType: filters.industry ? industryLabelToTenantType(filters.industry) : undefined,
    size: filters.size ?? undefined,
    includeArchived: opts.includeArchived || undefined,
    sortBy: ORG_SORT_TO_SERVER[sort.key],
    sortOrder: sort.dir,
    limit: 50,
  };
}

/** Default org toolbar state — shared by dashboard mount and route prefetch. */
export const DEFAULT_ORG_FILTERS: OrgFilters = {
  search: "",
  industry: null,
  size: null,
  showArchived: false,
  excludedColors: [],
};

export function defaultIamTenantsListFilters(includeArchived = false): IamTenantsListFilters {
  return toIamTenantsListFilters(DEFAULT_ORG_FILTERS, { includeArchived });
}

export const DEFAULT_INVITES_SORT: InviteSortState = { key: "createdAt", dir: "desc" };

export function toInvitesListFilters(
  filters: InvitesFilters,
  searchOverride?: string,
  sort: InviteSortState = DEFAULT_INVITES_SORT,
): InviteListFilters {
  // Only email/createdAt/expiresAt are orderable server-side; status and
  // sentBy are sorted client-side, so fall back to the default order here.
  // Narrowing in the ternary (rather than a separate boolean) lets TS infer
  // the literal `sortBy` union instead of the wider `InviteSortKey`.
  const serverSortBy =
    sort.key === "email" || sort.key === "expiresAt" || sort.key === "createdAt"
      ? sort.key
      : undefined;
  return {
    search: (searchOverride ?? filters.search).trim() || undefined,
    status: filters.status === "all" ? undefined : filters.status,
    tenantId: filters.tenantId ?? undefined,
    sortBy: serverSortBy ?? "createdAt",
    sortOrder: serverSortBy ? sort.dir : "desc",
    limit: 50,
  };
}

/** Default invites tab state — shared by dashboard mount and route prefetch. */
export const DEFAULT_INVITES_FILTERS: InvitesFilters = {
  search: "",
  status: "all",
  tenantId: null,
};

export function defaultInvitesListFilters(): InviteListFilters {
  return toInvitesListFilters(DEFAULT_INVITES_FILTERS);
}

const SERVER_ROLE_SORT_KEYS = new Set<RoleSortKey>(["name", "tenantName", "memberCount", "updatedAt"]);

export const DEFAULT_ROLE_SORT: RoleSortState = { key: "name", dir: "asc" };

function isServerRoleSort(key: RoleSortKey): key is IamAccessSortBy {
  return SERVER_ROLE_SORT_KEYS.has(key);
}

function compareRoleRows(a: RoleRow, b: RoleRow, sort: RoleSortState): number {
  const dir = sort.dir === "asc" ? 1 : -1;

  switch (sort.key) {
    case "name":
      return dir * a.name.localeCompare(b.name);
    case "tenantName":
      return dir * a.tenantName.localeCompare(b.tenantName);
    case "memberCount":
      return dir * (a.memberCount - b.memberCount);
    case "updatedAt":
      return dir * (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());
    case "description":
      return dir * (a.description ?? "").localeCompare(b.description ?? "");
    case "permissionCount":
      return dir * (a.permissionCount - b.permissionCount);
    case "type":
      return dir * a.type.localeCompare(b.type);
    default:
      return 0;
  }
}

/** Client-side sort for columns the access list API does not order by. */
export function sortRoleRows(rows: RoleRow[], sort: RoleSortState): RoleRow[] {
  if (isServerRoleSort(sort.key)) return rows;
  return [...rows].sort((a, b) => compareRoleRows(a, b, sort));
}

export function toIamAccessListFilters(
  filters: RoleFilters,
  opts: { includeArchived: boolean; searchOverride?: string; sort?: RoleSortState },
): IamAccessListFilters {
  const sort = opts.sort ?? DEFAULT_ROLE_SORT;
  const sortBy: IamAccessSortBy = isServerRoleSort(sort.key) ? sort.key : "name";

  return {
    search: (opts.searchOverride ?? filters.search).trim() || undefined,
    tenantId: filters.orgId ?? undefined,
    type: filters.type ? filters.type.toUpperCase() as "SYSTEM" | "CUSTOM" : undefined,
    includeArchived: opts.includeArchived || undefined,
    sortBy,
    sortOrder: isServerRoleSort(sort.key) ? sort.dir : DEFAULT_ROLE_SORT.dir,
    limit: 50,
  };
}

export function mapIamAccessToRoleRow(item: IamAccessListItem): RoleRow {
  return {
    id: item.id,
    name: item.name,
    description: item.description,
    tenantId: item.tenantId,
    tenantName: item.tenantName,
    tenantType: item.tenantType,
    tenantLogoUrl: item.tenantLogoUrl,
    tenantBrandColor: item.tenantBrandColor,
    memberCount: item.memberCount,
    permissionCount: item.permissionCount,
    type: item.type === "SYSTEM" ? "system" : "custom",
    updatedAt: item.updatedAt,
    isArchived: !!item.tenantDeletedAt || !!item.deletedAt,
  };
}

export function flattenInfinitePages<T>(pages: Array<{ items: T[] }> | undefined): T[] {
  if (!pages) return [];
  return pages.flatMap((p) => p.items);
}
